import type { ReactNode } from 'react';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { SignatureCapture } from './signature-capture';
import type { InteractiveSigningMethod } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signing';
import type { SigningSessionResponse } from '@/api/schemas/agreements.types';

const METHOD_OPTIONS = [
  { value: 'drawn', label: 'Drawn signature' },
  { value: 'uploaded', label: 'Uploaded signature image' },
];

/**
 * Capture the signature image and the signer's identity.
 *
 * Capturing an image does NOT sign. It produces an object reference that the final `sign`
 * call carries. The image is never presented as a certificate-backed digital signature.
 */
export function SignatureSection(props: {
  executionId: string;
  ensureSession: (method: InteractiveSigningMethod) => Promise<SigningSessionResponse>;
  signingMethod: InteractiveSigningMethod;
  onMethodChange: (method: InteractiveSigningMethod) => void;
  onUploaded: (objectKey: string, mimeType: 'image/png' | 'image/jpeg', byteSize: number) => void;
  signerName: string;
  onSignerNameChange: (value: string) => void;
  signerTitle: string;
  onSignerTitleChange: (value: string) => void;
}): ReactNode {
  return (
    <div className="space-y-4">
      <Select
        label="Signing method"
        options={METHOD_OPTIONS}
        value={props.signingMethod}
        onChange={(event) =>
          props.onMethodChange(event.target.value as InteractiveSigningMethod)
        }
      />
      <SignatureCapture
        executionId={props.executionId}
        ensureSession={props.ensureSession}
        signingMethod={props.signingMethod}
        onUploaded={props.onUploaded}
      />
      <Input
        label="Your name"
        value={props.signerName}
        onChange={(event) => props.onSignerNameChange(event.target.value)}
      />
      <Input
        label="Your title (optional)"
        value={props.signerTitle}
        onChange={(event) => props.onSignerTitleChange(event.target.value)}
      />
    </div>
  );
}
